import React, { useEffect, useMemo, useState } from 'react';
import { FaPrint } from 'react-icons/fa';
import './PrintPanel.css';

const inchesPerMeter = 39.37;
const initialResolution = 156543.03392804097;

const getRealDPI = () => 96 * window.devicePixelRatio;

const scaleToZoom = (scale) => {
    const dpi = getRealDPI();
    const scaleValue = parseInt(scale.replace('1:', '').replace(/,/g, ''), 10);
    const metersPerPixel = scaleValue / (dpi * inchesPerMeter);
    return Math.log2(initialResolution / metersPerPixel);
};

const hasValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string" && value.trim() === "") return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
};

const formatValue = (value) => {
    if (!hasValue(value)) return "prazno";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
};

const normalizeSelectedFeatures = (selectedFeature) => {
    if (!selectedFeature) return [];
    if (Array.isArray(selectedFeature)) return selectedFeature.flat().filter(Boolean);
    return [selectedFeature];
};

const getFeatureKey = (feature, index) => {
    return String(
        feature?.id ??
        feature?.properties?.id ??
        feature?.properties?.fid ??
        feature?.properties?.OBJECTID ??
        feature?.properties?.ogc_fid ??
        `feature-${index}`
    );
};

const PrintPanel = ({
    handlePrint,
    selectedFeature,
    setViewState,
    setActiveMode,
    activeMode,
    userInfo,
    mapRef
}) => {
    const [scale, setScale] = useState("");
    const [openIndexes, setOpenIndexes] = useState([0]);
    const [fieldSelection, setFieldSelection] = useState({});

    const selectedFeatures = useMemo(
        () => normalizeSelectedFeatures(selectedFeature),
        [selectedFeature]
    );

    useEffect(() => {
        setFieldSelection((prev) => {
            const next = {};
            let changed = false;

            selectedFeatures.forEach((feature, index) => {
                const featureKey = getFeatureKey(feature, index);
                const props = feature?.properties || {};
                const previousForFeature = prev[featureKey] || {};

                next[featureKey] = {};

                Object.entries(props).forEach(([key, value]) => {
                    if (Object.prototype.hasOwnProperty.call(previousForFeature, key)) {
                        next[featureKey][key] = previousForFeature[key];
                    } else {
                        next[featureKey][key] = hasValue(value);
                        changed = true;
                    }
                });
            });

            if (Object.keys(prev).join("|") !== Object.keys(next).join("|")) {
                changed = true;
            }

            return changed ? next : prev;
        });
    }, [selectedFeatures]);

    useEffect(() => {
        if (!scale) return;

        const zoom = scaleToZoom(scale);
        const center = mapRef?.current?.getMap?.()?.getCenter?.();

        setViewState(prev => ({
            ...prev,
            zoom,
            longitude: center?.lng ?? prev.longitude,
            latitude: center?.lat ?? prev.latitude
        }));
    }, [scale, setViewState, mapRef]);

    const handleToggleField = (featureKey, fieldName) => {
        setFieldSelection((prev) => ({
            ...prev,
            [featureKey]: {
                ...(prev[featureKey] || {}),
                [fieldName]: !(prev[featureKey]?.[fieldName] ?? false)
            }
        }));
    };

    const toggleIndex = (index) => {
        setOpenIndexes((prev) =>
            prev.includes(index)
                ? prev.filter((item) => item !== index)
                : [...prev, index]
        );
    };

    const buildPrintSelection = () => {
        return selectedFeatures.map((feature, index) => {
            const featureKey = getFeatureKey(feature, index);
            const props = feature?.properties || {};
            const selection = fieldSelection[featureKey] || {};

            const filteredProperties = Object.entries(props).reduce((acc, [key, value]) => {
                if (selection[key] === true) {
                    acc[key] = value;
                }
                return acc;
            }, {});

            return {
                ...feature,
                properties: filteredProperties
            };
        });
    };

    const onPrintClick = () => {
        handlePrint(scale, buildPrintSelection());
    };

    return (
        <div className="print-panel">
            <label className="print-field">
                <span className="print-label">Razmjera:</span>

                <select
                    value={scale}
                    onChange={(e) => setScale(e.target.value)}
                    className="print-select"
                >
                    <option value="">-- Izaberite --</option>
                    <option value="1:500000">1:500,000</option>
                    <option value="1:250000">1:250,000</option>
                    <option value="1:100000">1:100,000</option>
                    <option value="1:50000">1:50,000</option>
                    <option value="1:25000">1:25,000</option>
                    <option value="1:10000">1:10,000</option>
                    <option value="1:5000">1:5,000</option>
                    <option value="1:2500">1:2,500</option>
                    <option value="1:1000">1:1,000</option>
                    <option value="1:500">1:500</option>
                    <option value="1:250">1:250</option>
                    <option value="1:100">1:100</option>
                </select>
            </label>

            <label className="toggle">
                <span>Izaberite objekte</span>

                <input
                    type="checkbox"
                    checked={activeMode === "selectMultiple"}
                    onChange={(e) => {
                        setActiveMode(e.target.checked ? "selectMultiple" : "select");
                    }}
                />

                <span className="slider"></span>
            </label>

            {selectedFeatures.length > 0 && (
                <div className="print-attributes">
                    <h4 className="print-attributes-title">
                        Atributi za štampu:
                    </h4>

                    <div className="print-objects">
                        {selectedFeatures.map((feature, index) => {
                            const featureKey = getFeatureKey(feature, index);
                            const props = feature?.properties || {};
                            const isOpen = openIndexes.includes(index);

                            return (
                                <div key={featureKey} className="print-object-card">
                                    <button
                                        type="button"
                                        onClick={() => toggleIndex(index)}
                                        className="print-object-header"
                                    >
                                        <span>
                                            Objekat {index + 1}: {feature?.id || 'Bez ID-a'}
                                        </span>
                                        <span>{isOpen ? '▲' : '▼'}</span>
                                    </button>

                                    {isOpen && (
                                        <div className="print-attribute-list">
                                            {Object.entries(props).map(([key, value]) => {
                                                const checked = fieldSelection[featureKey]?.[key] === true;
                                                const valueExists = hasValue(value);

                                                return (
                                                    <label
                                                        key={key}
                                                        className={`print-attribute-row ${checked ? 'is-selected' : ''}`}
                                                    >
                                                        <span className="print-attribute-check">
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                onChange={() => handleToggleField(featureKey, key)}
                                                            />
                                                        </span>

                                                        <span className="print-attribute-name">
                                                            {key}
                                                        </span>

                                                        <span className={`print-attribute-value ${!valueExists ? 'is-empty' : ''}`}>
                                                            {formatValue(value)}
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <button
                onClick={onPrintClick}
                className={`buttonPrint ${scale ? 'enabled' : 'disabled'}`}
                disabled={!scale}
            >
                <FaPrint /> Štampaj
            </button>
        </div>
    );
};

export default PrintPanel;